// src/types/result.ts
function ok(value) {
  return { ok: true, value };
}
function err(error) {
  return { ok: false, error };
}
function mapResult(result, fn) {
  if (result.ok) return ok(fn(result.value));
  return result;
}
function flatMapResult(result, fn) {
  if (result.ok) return fn(result.value);
  return result;
}
function unwrapResult(result) {
  if (result.ok) return result.value;
  throw result.error;
}

// src/types/id.ts
function newId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = bytes[6] & 15 | 64;
    bytes[8] = bytes[8] & 63 | 128;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === "x" ? r : r & 3 | 8;
    return v.toString(16);
  });
}

// src/utils/date.ts
function nowISO() {
  return (/* @__PURE__ */ new Date()).toISOString();
}
function toISO(date) {
  return date.toISOString();
}
function fromISO(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) {
    throw new RangeError(`Invalid ISO date string: "${iso}"`);
  }
  return d;
}
function daysBetweenISO(a, b) {
  const msPerDay = 864e5;
  return Math.floor((fromISO(a).getTime() - fromISO(b).getTime()) / msPerDay);
}
function daysAgoISO(n, from = /* @__PURE__ */ new Date()) {
  return new Date(from.getTime() - n * 864e5).toISOString();
}
function compareISO(a, b) {
  return fromISO(a).getTime() - fromISO(b).getTime();
}
function isPastISO(iso) {
  return fromISO(iso).getTime() < Date.now();
}
function toDatePart(iso) {
  return iso.slice(0, 10);
}

// src/utils/url.ts
function normalizeUrl(raw) {
  let url;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  url.hash = "";
  if (url.protocol === "https:" && url.port === "443" || url.protocol === "http:" && url.port === "80") {
    url.port = "";
  }
  if (url.pathname.length > 1 && url.pathname.endsWith("/")) {
    url.pathname = url.pathname.slice(0, -1);
  }
  return url.toString();
}
function parseUrl(raw) {
  try {
    return new URL(raw.trim());
  } catch {
    return null;
  }
}
function extractOrigin(raw) {
  const url = parseUrl(raw);
  return url ? url.origin : null;
}
function isHttpUrl(raw) {
  const url = parseUrl(raw);
  return url !== null && (url.protocol === "http:" || url.protocol === "https:");
}
function isLocalPath(raw) {
  const trimmed = raw.trim();
  if (trimmed.startsWith("/")) return true;
  if (/^[A-Za-z]:[/\\]/.test(trimmed)) return true;
  if (trimmed.startsWith("\\\\")) return true;
  return false;
}
function joinUrl(base, path) {
  try {
    return new URL(path, base).toString();
  } catch {
    return null;
  }
}
export {
  compareISO,
  daysAgoISO,
  daysBetweenISO,
  err,
  extractOrigin,
  flatMapResult,
  fromISO,
  isHttpUrl,
  isLocalPath,
  isPastISO,
  joinUrl,
  mapResult,
  newId,
  normalizeUrl,
  nowISO,
  ok,
  parseUrl,
  toDatePart,
  toISO,
  unwrapResult
};
